import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';
import Container from '@components/ui/container';
import RicherText from '@blocks/RicherText';
import Divider from '@blocks/Divider';
import Subscription from '@blocks/SubscribeToNucleusCTA';
import ArticleHero from '@components/articles/article-hero';
import ArticlesAuthorInfo from '@containers/articles-author-info';
import { useUI } from '@contexts/ui.context';
import Button from '@components/ui/button';
import Template from '@components/layout/template';
import RenderBlocks from '@components/RenderBlocks';
import { API_URL, nullGetStaticProps } from '@lib/constants';
import Head from '@components/Head';
import { Post } from '@payload/types';
import NotFound from '@components/NotFound';
import InternalError from '@components/InternalError';
import RichTextContainer from '@components/ui/rich-text-container';
import ResourceGridSection from '@components/sections/resource-grid-section';
import StandaloneHeaderCluster from '@blocks/StandaloneHeaderCluster';

interface Props {
  post: Post;
  recommendedPosts: Post[];
  status?: number;
  isPreview: boolean;
}

export default function ArticlePage({
  post,
  recommendedPosts,
  status,
  isPreview,
}: Props) {
  if (status === 500) {
    return <InternalError />;
  }

  if (!post) {
    return <NotFound />;
  }

  if (!isPreview && post.status !== 'published') {
    return <NotFound />;
  }

  const publishedDate: string = post.publishedDate
    ? post.publishedDate
    : //@ts-ignore
      post.createdAt;

  const { openModal, setModalView, setModalData } = useUI();

  const hasDownloadableResource = post.resource?.hasDownload ?? false;
  //@ts-ignore
  const isDownloadWalled: boolean = post?.resource?.download?.isWalled ?? false;
  //@ts-ignore
  const downloadUrl: string = post?.resource?.download?.url;
  const resourceId: string =
    //@ts-ignore
    hasDownloadableResource && post!.resource!.download!.id!.toString();

  function handleDownload(resourceId: string) {
    if (hasDownloadableResource && isDownloadWalled) {
      setModalView('DOWNLOAD_VIEW');
      setModalData({ resourceId: resourceId });
      return openModal();
    } else {
      return window.open(downloadUrl, '_blank')!.focus();
    }
  }

  const recommendedPostsTitle = {
    subTitle: 'NUCLEUS',
    title: 'Recommended Posts',
    options: { hasButtons: false },
  };

  return (
    <React.Fragment>
      <Head
        title={`${post.meta?.title || post.title} | Telesoft`}
        description={post.meta?.description || post.summary}
        keywords={post.meta?.keywords}
      >
        <meta
          property="og:title"
          content={`${post.meta?.title || post?.title} | Telesoft`}
          key="title"
        />
        <meta
          property="og:description"
          content={`${post.meta?.description || post?.summary}`}
          key="description"
        />
      </Head>

      <Template previewMode={isPreview}>
        <Container>
          <ArticleHero
            downloadClick={() => handleDownload(resourceId)}
            hasDownload={hasDownloadableResource}
            article={post}
          />
          <div id="article" className="relative">
            <ArticlesAuthorInfo
              author={post?.author}
              publishedDate={publishedDate}
              article={post}
            />
            <Divider />
            <RichTextContainer>
              <RicherText className="font-content" content={post?.content} />
            </RichTextContainer>
            {hasDownloadableResource && (
              <>
                <Divider />
                <Button
                  type="button"
                  variant="cta"
                  className=""
                  onClick={() => handleDownload(resourceId)}
                >
                  Download resource
                </Button>
              </>
            )}
            <RenderBlocks blocks={post.blocks} />
          </div>
        </Container>
        <Divider />
        <StandaloneHeaderCluster {...recommendedPostsTitle} />
        <Container>
          <ResourceGridSection data={recommendedPosts} />
        </Container>
        <Divider />
        <Subscription />
      </Template>
    </React.Fragment>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const res = await fetch(`${API_URL}/posts?limit=500`);
  const json = await res.json();

  return {
    paths: json.docs.map(({ slug }: { slug: string }) => ({
      params: { slug: slug },
    })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context?.params?.slug;
  const isPreview = !!context.preview;

  const res = await fetch(
    `${API_URL}/posts?depth=4&where[slug][equals]=${slug}`
  );
  if (!res.ok) {
    return {
      ...nullGetStaticProps,
      props: {
        status: 500,
      },
    };
  }

  const json = await res.json();
  if (json.totalDocs === 0) {
    return nullGetStaticProps;
  }

  const post = json.docs[0];

  const catRes = await fetch(`${API_URL}/posts`);
  const catPosts = await catRes.json();
  const recommendedPosts = catPosts.docs.splice(
    Math.floor(Math.random() * catPosts.length),
    3
  );

  return {
    props: { post, recommendedPosts, isPreview },
    revalidate: 10,
  };
};
